// src/Pages/ItBacklog/ItBacklogPublic.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ContentWMax from "../../Components/PageLayout/ContentWMax";
import PageTitle from "../../Components/PageTitle/PageTitle";
import styles from "./ItBacklog.module.scss";
import api from "../../services/api";

/**
 * Публичная страница:
 * - только просмотр задач в "В очереди" и "В работе"
 * - без создания/редактирования/изменения статусов/комментариев
 * - есть просмотр описания задачи (модалка)
 */

const STATUS_META = {
    queue: { label: "В очереди", tone: "queue" },
    in_progress: { label: "В работе", tone: "progress" },
    done: { label: "Готово", tone: "done" },
};

const PRIORITY_META = {
    low: { label: "Низкий", tone: "low" },
    medium: { label: "Средний", tone: "medium" },
    high: { label: "Высокий", tone: "high" },
};

function pad2(n) {
    return String(n).padStart(2, "0");
}
function toIsoDate(d) {
    const y = d.getFullYear();
    const m = pad2(d.getMonth() + 1);
    const day = pad2(d.getDate());
    return `${y}-${m}-${day}`;
}
function parseIso(iso) {
    const [y, m, d] = String(iso || "").split("-").map(Number);
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    dt.setHours(0, 0, 0, 0);
    return dt;
}
function formatDate(iso) {
    const dt = parseIso(iso);
    if (!dt) return iso || "—";
    return `${pad2(dt.getDate())}.${pad2(dt.getMonth() + 1)}.${dt.getFullYear()}`;
}

function getNameById(list, id) {
    return list.find((x) => x.id === id)?.name || "—";
}

function isWeekend(dt) {
    const wd = dt.getDay();
    return wd === 0 || wd === 6;
}

/**
 * Планирование:
 * - fixedDays: ["YYYY-MM-DD", ...] — задача выполняется только в эти дни
 * - иначе: планируем от startDate..dueDate по hoursPerDay, пока не закроем estimateHours
 * - weekdays: [0..6] — выбранные дни недели (если задано и не пусто — фильтруем по ним)
 * - weekends: по умолчанию пропускаем (можно включить allowWeekends), если weekdays не задан
 */
function buildScheduleForTask(task) {
    const res = new Map(); // iso -> hours
    if (!task) return res;

    const hoursPerDay = Number(task.planning?.hoursPerDay || 0);
    const estimate = Number(task.planning?.estimateHours || 0);
    const allowWeekends = Boolean(task.planning?.allowWeekends);
    const fixedDays = Array.isArray(task.planning?.fixedDays) ? task.planning.fixedDays : [];
    const weekdays = Array.isArray(task.planning?.weekdays) ? task.planning.weekdays : null; // [0..6]

    if (fixedDays.length) {
        const per = hoursPerDay > 0 ? hoursPerDay : fixedDays.length ? estimate / fixedDays.length : 0;
        fixedDays.forEach((iso) => {
            if (!iso) return;
            const hrs = Math.max(0, Number(per || 0));
            if (hrs > 0) res.set(iso, (res.get(iso) || 0) + hrs);
        });
        return res;
    }

    const start = parseIso(task.planning?.startDate);
    const due = parseIso(task.planning?.dueDate);
    if (!start || !due || !hoursPerDay || !estimate) return res;

    let remaining = estimate;
    for (let d = new Date(start); d <= due && remaining > 0; d.setDate(d.getDate() + 1)) {
        const day = new Date(d);
        day.setHours(0, 0, 0, 0);

        if (weekdays && weekdays.length) {
            if (!weekdays.includes(day.getDay())) continue;
        } else {
            if (!allowWeekends && isWeekend(day)) continue;
        }

        const iso = toIsoDate(day);
        const hrs = Math.min(hoursPerDay, remaining);
        if (hrs > 0) res.set(iso, (res.get(iso) || 0) + hrs);
        remaining -= hrs;
    }
    return res;
}

/** API */
async function apiGetDepartments() {
    const { data } = await api.get("/api/it/departments");
    return data?.items || [];
}
async function apiGetOwners() {
    const { data } = await api.get("/api/it/owners");
    return data?.items || [];
}
async function apiGetTasksPublic() {
    // Публично показываем только queue + in_progress
    const { data } = await api.get("/api/it/tasks", {
        params: { status: undefined }, // фильтруем на фронте (чтобы не зависеть от бэка)
    });
    return data?.items || [];
}

/** Read-only модалка просмотра задачи (в т.ч. описание) */
function TaskModalPublic({ task, onClose, owners, departments }) {
    const backdropRef = useRef(null);
    const mouseDownOnBackdrop = useRef(false);

    if (!task) return null;

    const statusMeta = STATUS_META[task.status] || STATUS_META.queue;
    const prioMeta = PRIORITY_META[task.priority] || PRIORITY_META.medium;

    const schedule = buildScheduleForTask(task);
    const scheduleItems = Array.from(schedule.entries()).sort(([a], [b]) => a.localeCompare(b));

    return (
        <div
            ref={backdropRef}
            className={styles.modalBackdrop}
            onMouseDown={(e) => {
                mouseDownOnBackdrop.current = e.target === backdropRef.current;
            }}
            onMouseUp={(e) => {
                if (mouseDownOnBackdrop.current && e.target === backdropRef.current) onClose();
                mouseDownOnBackdrop.current = false;
            }}
            role="presentation"
        >
            <div
                className={styles.modal}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                role="presentation"
            >
                <div className={styles.modalHeader}>
                    <div>
                        <div className={styles.modalTitle}>
                            {task.id}: {task.title}
                        </div>
                        <div className={styles.modalSub}>
                            {getNameById(departments, task.departmentId)} · Создано {formatDate(task.createdAt)}
                        </div>
                    </div>

                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Закрыть">
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.modalGrid}>
                        <div className={styles.modalCard}>
                            <div className={styles.modalLabel}>Статус</div>
                            <div className={styles.modalValue}>
                                <span className={`${styles.badge} ${styles[statusMeta.tone]}`}>{statusMeta.label}</span>
                            </div>
                        </div>

                        <div className={styles.modalCard}>
                            <div className={styles.modalLabel}>Приоритет</div>
                            <div className={styles.modalValue}>
                                <span className={`${styles.prio} ${styles[prioMeta.tone]}`}>{prioMeta.label}</span>
                            </div>
                        </div>

                        <div className={styles.modalCard}>
                            <div className={styles.modalLabel}>Исполнитель</div>
                            <div className={styles.modalValue}>{task.ownerId ? getNameById(owners, task.ownerId) : "Не назначен"}</div>
                        </div>

                        <div className={styles.modalCard}>
                            <div className={styles.modalLabel}>Теги</div>
                            <div className={styles.modalValue}>{task.tags?.length ? task.tags.join(", ") : "—"}</div>
                        </div>
                    </div>

                    {/* ОПИСАНИЕ */}
                    <div className={styles.modalSection}>
                        <div className={styles.modalLabel}>Описание</div>
                        <div className={styles.modalText}>{task.description || "—"}</div>
                    </div>

                    {/* План по дням */}
                    <div className={styles.modalSection}>
                        <div className={styles.modalLabel}>План по дням</div>
                        {scheduleItems.length ? (
                            <div className={styles.scheduleList}>
                                {scheduleItems.map(([iso, hrs]) => (
                                    <div key={iso} className={styles.scheduleItem}>
                                        <span className={styles.scheduleDate}>{formatDate(iso)}</span>
                                        <span className={styles.scheduleHours}>{hrs.toFixed(1)} ч</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.mutedBlock}>Планирование не заполнено.</div>
                        )}
                    </div>

                    {/* Подзадачи (только просмотр) */}
                    {task.subtasks?.length ? (
                        <div className={styles.modalSection}>
                            <div className={styles.modalLabel}>Чек-лист</div>
                            <div className={styles.subtasks}>
                                {task.subtasks.map((s) => (
                                    <label key={String(s.id)} className={styles.subtaskRow}>
                                        <input type="checkbox" checked={Boolean(s.done)} readOnly disabled />
                                        <span className={s.done ? styles.subtaskDone : ""}>{s.title}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default function ItBacklogPublic() {
    const [departments, setDepartments] = useState([]);
    const [owners, setOwners] = useState([]);
    const [tasks, setTasks] = useState([]);

    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    // фильтры
    const [query, setQuery] = useState("");
    const [deptFilter, setDeptFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");

    // UI
    const [viewMode, setViewMode] = useState("kanban"); // kanban | table
    const [selectedTask, setSelectedTask] = useState(null);

    useEffect(() => {
        let mounted = true;

        (async () => {
            setLoading(true);
            setErrorMsg("");
            try {
                const [deps, own, t] = await Promise.all([apiGetDepartments(), apiGetOwners(), apiGetTasksPublic()]);
                if (!mounted) return;
                setDepartments(deps);
                setOwners(own);

                // показываем только "В работе" и "В очереди"
                const onlyPublic = (t || []).filter((x) => x.status === "in_progress" || x.status === "queue");
                setTasks(onlyPublic);
            } catch (e) {
                if (!mounted) return;
                setErrorMsg("Не удалось загрузить публичный бэклог.");
            } finally {
                if (!mounted) return;
                setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, []);

    // синхронизируем selectedTask из tasks
    useEffect(() => {
        if (!selectedTask?.id) return;
        const fresh = tasks.find((t) => t.id === selectedTask.id);
        if (fresh) setSelectedTask(fresh);
    }, [tasks, selectedTask?.id]);

    const filteredTasks = useMemo(() => {
        const q = query.trim().toLowerCase();
        return tasks.filter((t) => {
            const okDept = deptFilter === "all" ? true : t.departmentId === deptFilter;
            const okPrio = priorityFilter === "all" ? true : t.priority === priorityFilter;

            const searchable = `${t.id} ${t.title} ${t.description} ${(t.tags || []).join(" ")}`.toLowerCase();
            const okQuery = q.length === 0 ? true : searchable.includes(q);

            return okDept && okPrio && okQuery;
        });
    }, [tasks, query, deptFilter, priorityFilter]);

    const grouped = useMemo(() => {
        const map = { in_progress: [], queue: [] };
        for (const t of filteredTasks) {
            if (t.status === "in_progress") map.in_progress.push(t);
            if (t.status === "queue") map.queue.push(t);
        }
        return map;
    }, [filteredTasks]);

    return (
        <ContentWMax>
            <PageTitle title="Публичный IT бэклог (только просмотр)" />

            <div className={styles.page}>
                {loading ? <div className={styles.mutedBlock}>Загрузка...</div> : null}
                {errorMsg ? <div className={styles.mutedBlock}>{errorMsg}</div> : null}

                <div className={styles.toolbar}>
                    <div className={styles.searchWrap}>
                        <input
                            className={styles.search}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Поиск: ID, название, теги, описание..."
                        />
                    </div>

                    <div className={styles.filters}>
                        <select className={styles.select} value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
                            <option value="all">Все отделы</option>
                            {departments.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>

                        <select className={styles.select} value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                            <option value="all">Любой приоритет</option>
                            <option value="high">Высокий</option>
                            <option value="medium">Средний</option>
                            <option value="low">Низкий</option>
                        </select>

                        <div className={styles.viewToggle}>
                            <button
                                type="button"
                                className={`${styles.toggleBtn} ${viewMode === "kanban" ? styles.active : ""}`}
                                onClick={() => setViewMode("kanban")}
                            >
                                Канбан
                            </button>
                            <button
                                type="button"
                                className={`${styles.toggleBtn} ${viewMode === "table" ? styles.active : ""}`}
                                onClick={() => setViewMode("table")}
                            >
                                Таблица
                            </button>
                        </div>
                    </div>
                </div>

                {viewMode === "kanban" ? (
                    <div className={styles.kanban}>
                        {["in_progress", "queue"].map((col) => (
                            <div key={col} className={styles.column}>
                                <div className={styles.columnHeader}>
                                    <div className={styles.columnTitle}>
                                        <span className={`${styles.badge} ${styles[STATUS_META[col].tone]}`}>{STATUS_META[col].label}</span>
                                    </div>
                                    <div className={styles.columnCount}>{grouped[col].length}</div>
                                </div>

                                <div className={styles.cards}>
                                    {grouped[col].map((t) => (
                                        <button key={t.id} type="button" className={styles.taskCard} onClick={() => setSelectedTask(t)}>
                                            <div className={styles.taskTop}>
                                                <div className={styles.taskId}>{t.id}</div>
                                                <span className={`${styles.prio} ${styles[PRIORITY_META[t.priority]?.tone || "medium"]}`}>
                                                    {PRIORITY_META[t.priority]?.label || "Средний"}
                                                </span>
                                            </div>

                                            <div className={styles.taskTitle}>{t.title}</div>

                                            <div className={styles.taskMetaRow}>
                                                <div className={styles.taskMeta}>
                                                    <span className={styles.metaLabel}>Отдел:</span> {getNameById(departments, t.departmentId)}
                                                </div>
                                                <div className={styles.taskMeta}>
                                                    <span className={styles.metaLabel}>Исполнитель:</span>{" "}
                                                    {t.ownerId ? getNameById(owners, t.ownerId) : "Не назначен"}
                                                </div>
                                            </div>

                                            <div className={styles.taskBottom}>
                                                <div className={styles.smallText}>Создано: {formatDate(t.createdAt)}</div>
                                                <div className={styles.smallText}>
                                                    План:{" "}
                                                    {(() => {
                                                        const s = buildScheduleForTask(t);
                                                        const totalH = Array.from(s.values()).reduce((a, b) => a + b, 0);
                                                        return totalH ? `${totalH.toFixed(0)} ч` : "—";
                                                    })()}
                                                </div>
                                            </div>

                                            {t.tags?.length ? (
                                                <div className={styles.tags}>
                                                    {t.tags.slice(0, 3).map((tag) => (
                                                        <span key={tag} className={styles.tag}>
                                                            {tag}
                                                        </span>
                                                    ))}
                                                    {t.tags.length > 3 ? <span className={styles.tag}>+{t.tags.length - 3}</span> : null}
                                                </div>
                                            ) : null}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>Задача</th>
                                <th>Отдел</th>
                                <th>Статус</th>
                                <th>Приоритет</th>
                                <th>Исполнитель</th>
                                <th>Создано</th>
                                <th>План (ч)</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredTasks.map((t) => {
                                const s = buildScheduleForTask(t);
                                const totalH = Array.from(s.values()).reduce((a, b) => a + b, 0);
                                return (
                                    <tr key={t.id} onClick={() => setSelectedTask(t)} className={styles.rowClickable}>
                                        <td className={styles.mono}>{t.id}</td>
                                        <td>{t.title}</td>
                                        <td>{getNameById(departments, t.departmentId)}</td>
                                        <td>
                                                <span className={`${styles.badge} ${styles[STATUS_META[t.status]?.tone || "queue"]}`}>
                                                    {STATUS_META[t.status]?.label || t.status}
                                                </span>
                                        </td>
                                        <td>
                                                <span className={`${styles.prio} ${styles[PRIORITY_META[t.priority]?.tone || "medium"]}`}>
                                                    {PRIORITY_META[t.priority]?.label || "Средний"}
                                                </span>
                                        </td>
                                        <td>{t.ownerId ? getNameById(owners, t.ownerId) : "Не назначен"}</td>
                                        <td>{formatDate(t.createdAt)}</td>
                                        <td>{totalH ? `${totalH.toFixed(1)} ч` : "—"}</td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}

                <TaskModalPublic
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                    owners={owners}
                    departments={departments}
                />
            </div>
        </ContentWMax>
    );
}
